import i18next, { type i18n as I18nInstance } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import ICU from "i18next-icu";
import { initReactI18next } from "react-i18next";
import {
	ACTIVE_LOCALES,
	DEFAULT_LOCALE,
	ECOSYSTEM_LOCALES,
	type EcosystemLocale,
	FALLBACK_LOCALE,
	LOCALE_META,
	localesForPicker,
	normalizeLocale,
} from "../constants/locales.js";

/**
 * Общая инициализация i18n для обоих фронтов.
 *
 * ICU-плагин обязателен: множественные формы и падежи («3 200 Баллов сгорают»)
 * без него не собрать. Локаль хранится в профиле (TEXT ISO 639-1) — детектор
 * нужен только до первого входа.
 */
export type I18nResources = Record<string, Record<string, unknown>>;

export type CreateI18nOptions = {
	/** Ресурсы вида `{ en: { common: {...} }, ru: { common: {...} } }`. */
	resources: Record<string, I18nResources>;
	/** Локаль профиля, если пользователь уже известен. */
	locale?: string | undefined;
	/** Пространства имён; первое — по умолчанию. */
	namespaces: readonly string[];
	debug?: boolean;
};

export function createI18n(options: CreateI18nOptions): I18nInstance {
	const instance = i18next.createInstance();

	void instance
		// `bindI18nStore: "added"` — не настройка производительности, а
		// единственное, что чинит язык строк, отрисованных ДО приезда словаря.
		//
		// Словари 20 локалей грузятся лениво (`addResourceBundle` в
		// `lib/i18n.ts` каждого фронта), а `i18next-icu` кэширует скомпилированное
		// сообщение по ключу `<lng>.<ns>.<key>`. Пока русского словаря нет,
		// `t("cookies.banner.title")` резолвится фолбэком в английский — и
		// плагин кладёт АНГЛИЙСКОЕ сообщение под ключ `ru.legal.…`
		// (`memoizeFallback` от этого не спасает: он отсекает только
		// ненайденные ключи, а найденный в фолбэк-локали считается успешным).
		// Дальше словарь приезжает, хранилище становится верным, React
		// перерисовывается — а `t` до конца жизни страницы отдаёт English из
		// кэша. Ровно так баннер cookie (он монтируется в `__root.tsx` и
		// рисуется первым) оказывался английским на русской витрине, при
		// корректном `getResource("ru", …)`.
		//
		// С этой опцией плагин подписывается на `store.on("added")` и сбрасывает
		// свой кэш, когда словарь пополнился. Опция плагина отдельная от
		// `react.bindI18nStore` ниже: та управляет перерисовкой React, эта —
		// кэшем компилятора ICU, и одна другую не заменяет.
		.use(new ICU({ bindI18nStore: "added" }))
		.use(LanguageDetector)
		.use(initReactI18next)
		.init({
			resources: options.resources,
			// Все 21 локаль экосистемы (Д11). Локаль, которой нет в списке,
			// i18next молча приводит к фолбэку — поэтому список ровно один и
			// берётся из общего реестра, а не перечисляется в приложении.
			supportedLngs: [...ACTIVE_LOCALES],
			lng: options.locale ? normalizeLocale(options.locale) : undefined,
			// Фолбэк английский и только английский: непереведённый ключ обязан
			// показаться по-английски, а не по-русски (Д11). Физически это
			// обеспечено ещё и словарями — `pnpm i18n:sync` заводит недостающие
			// локали копией `en`, а не `ru`.
			fallbackLng: FALLBACK_LOCALE,
			ns: [...options.namespaces],
			defaultNS: options.namespaces[0] ?? "common",
			// Сервер присылает готовые ключи (`points.history.kind.*`,
			// `entitlements.source.*`, типы уведомлений) — они общие для разделов
			// и живут в `common`. Без фолбэка такой ключ выводился бы сырым в том
			// разделе, чей словарь оказался текущим.
			fallbackNS: "common",
			debug: options.debug ?? false,
			interpolation: { escapeValue: false },
			detection: {
				order: ["querystring", "localStorage", "navigator"],
				lookupQuerystring: "lang",
				lookupLocalStorage: "iec-id.locale",
				caches: ["localStorage"],
			},
			returnNull: false,
			// Перерисовка на ПОПОЛНЕНИЕ хранилища, а не только на смену языка.
			//
			// При 21 локали словари грузятся лениво: `addResourceBundle` кладёт
			// чанк уже после того, как язык переключился. По умолчанию
			// `bindI18nStore` пуст, и событие `added` проходит мимо React —
			// интерфейс остаётся на ключах или на английском до следующего
			// рендера по другой причине. Каждому приложению приходилось звать
			// `changeLanguage` повторно; здесь это решается один раз для всех.
			react: { bindI18nStore: "added" },
			// Русский текст в defaultValue уезжает в UI на всех языках —
			// ключ без перевода должен быть видимой дырой, а не «работающим» текстом.
			parseMissingKeyHandler: (key) => (options.debug ? `⟦${key}⟧` : key),
		});

	// Атрибут `lang` на <html> — не косметика: от него зависят перенос слов,
	// подбор шрифта под письменность и то, каким голосом читает скринридер.
	// i18next его не трогает, поэтому синхронизируем здесь — один раз на оба
	// фронта, а не в каждом переключателе языка.
	// `document` берётся через `globalThis`, а не напрямую: пакет собирается без
	// DOM-библиотеки (его импортирует и сервер), а тут единственное обращение
	// к странице на весь `shared`.
	const doc = (globalThis as { document?: { documentElement: { lang: string; dir: string } } })
		.document;
	if (doc) {
		const applyLang = (lng: string): void => {
			const code = normalizeLocale(lng);
			doc.documentElement.lang = code;
			doc.documentElement.dir = LOCALE_META[code].dir;
		};
		applyLang(instance.resolvedLanguage ?? instance.language ?? DEFAULT_LOCALE);
		// `dir` пишем рядом с `lang`: все 21 локали ltr, но правило «направление
		// задаётся вместе с языком» должно существовать до первой rtl-локали, а
		// не появляться вместе с ней.
		instance.on("languageChanged", applyLang);
	}

	return instance;
}

/**
 * Код в том виде, в каком его ждут контракты: две буквы ISO 639-1.
 * Детектор браузера отдаёт `ru-RU`, Google — `zh-CN`, сервер такой строки не примет.
 *
 * Тонкая обёртка над `normalizeLocale` из реестра локалей: приведение одно на
 * монорепо, а имя оставлено ради вызовов во фронтах.
 */
export function normalizeLocaleCode(raw: string | null | undefined): EcosystemLocale {
	return normalizeLocale(raw);
}

/**
 * Название языка на нём самом («Русский», «English», «Հայերեն»).
 *
 * Из общего реестра `LOCALE_META`, а не из `Intl.DisplayNames`: подписи обязаны
 * совпадать со StudyHub и Hero буква в букву (см. комментарий у реестра).
 * `Intl` остаётся фолбэком на код вне экосистемы — контент бывает на языке,
 * которого в списке ещё нет, и «PT» там честнее пустоты.
 */
export function localeEndonym(code: string): string {
	const locale = normalizeLocale(code);
	return LOCALE_META[locale].nativeName;
}

/**
 * Название языка средствами движка — для кодов ВНЕ экосистемы (язык материала,
 * язык документа), где эндонима у нас нет.
 *
 * `Intl.DisplayNames` бросает `RangeError` на коде, которого не знает ICU
 * сборки, и падает целиком экран, а не подпись. Отсюда try/catch и код в
 * верхнем регистре как честный фолбэк.
 */
export function displayNameOf(code: string, inLocale: string = FALLBACK_LOCALE): string {
	const raw = (code ?? "").trim();
	if (raw.length === 0) return "";
	try {
		const name = new Intl.DisplayNames([inLocale], { type: "language" }).of(raw);
		if (!name || name === raw) return raw.toUpperCase();
		return name.charAt(0).toLocaleUpperCase(inLocale) + name.slice(1);
	} catch {
		return raw.toUpperCase();
	}
}

/**
 * Название страны на языке интерфейса («Кипр», «Cyprus») по коду ISO 3166-1.
 *
 * Сосед `displayNameOf`, но `type: "region"`: тот отвечает за языки. Заведён
 * потому, что список стран в мастере звал `Intl.DisplayNames` напрямую — а он
 * бросает `RangeError` на локали, которой нет в ICU конкретной сборки движка,
 * и падает не подпись страны, а весь шаг онбординга. С двумя локалями это не
 * всплывало, с двадцатью одной всплывёт.
 */
export function regionNameOf(code: string, inLocale: string = FALLBACK_LOCALE): string {
	const raw = (code ?? "").trim();
	if (raw.length === 0) return "";
	try {
		return new Intl.DisplayNames([inLocale], { type: "region" }).of(raw) ?? raw.toUpperCase();
	} catch {
		return raw.toUpperCase();
	}
}

/**
 * Сравнение строк по правилам локали. Та же защита, что у `regionNameOf`:
 * `Intl.Collator` бросает на неизвестной движку локали, а сортировка списка не
 * стоит упавшего экрана.
 */
export function localeCompare(
	inLocale: string = FALLBACK_LOCALE,
): (a: string, b: string) => number {
	try {
		const collator = new Intl.Collator(inLocale);
		return (a, b) => collator.compare(a, b);
	} catch {
		return (a, b) => (a < b ? -1 : a > b ? 1 : 0);
	}
}

/** Компактный ярлык для триггера в шапке («RU», «CN»). */
export function localeShortLabel(code: string): string {
	return LOCALE_META[normalizeLocale(code)].shortLabel;
}

/** Направление письма локали — для `dir` на <html> и на изолированных вставках. */
export function localeDirection(code: string): "ltr" | "rtl" {
	return LOCALE_META[normalizeLocale(code)].dir;
}

export {
	ACTIVE_LOCALES,
	DEFAULT_LOCALE,
	ECOSYSTEM_LOCALES,
	FALLBACK_LOCALE,
	LOCALE_META,
	localesForPicker,
	normalizeLocale,
};
